import React from "react";
import { FormControl, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import LabeledField from "../../components/form/LabeledField";
import { PifDetailsValues, PifOption } from "./pifDetailsModel";
import { pifControlId } from "./WorkflowPifTextField";

interface WorkflowPifSelectFieldProps {
  fieldKey: string;
  label: string;
  options: PifOption[];
  values: PifDetailsValues;
  errors: Record<string, string>;
  onChange: (fieldKey: string, value: string) => void;
  /** The empty choice's text, e.g. "Choose a department…". */
  placeholder: string;
  required?: boolean;
  disabled?: boolean;
  hint?: string;
}

/** One pif-3 dropdown, keyed by its field key. It stores the option's ID, never its label. */
const WorkflowPifSelectField: React.FC<WorkflowPifSelectFieldProps> = ({
  fieldKey,
  label,
  options,
  values,
  errors,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  hint,
}) => {
  const id = pifControlId(fieldKey);
  const error = errors[fieldKey];
  const value = values[fieldKey] ?? "";
  // A saved ID the current list doesn't hold yet (still loading) shows as empty, not as a warning.
  const selectable = options.some((option) => option.id === value) ? value : "";
  const handleChange = (event: SelectChangeEvent) => onChange(fieldKey, event.target.value);

  return (
    <LabeledField controlId={id} label={label} required={required} error={error} hint={hint}>
      <FormControl size="small" fullWidth required={required} disabled={disabled} error={Boolean(error)}>
        <Select id={id} labelId={`${id}-label`} value={selectable} onChange={handleChange} displayEmpty>
          <MenuItem value="">{placeholder}</MenuItem>
          {options.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.text}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </LabeledField>
  );
};

export default WorkflowPifSelectField;
